import type { BlockSectionBg } from "./common/types";
import type { BlockWrapperBg } from "@/shared/components/BlockWrapper";
import { cn } from "@/lib/utils";

/** Dark and red section backgrounds use light foreground text. */
export function isTintedSectionBg(sectionBg?: BlockSectionBg | null): boolean {
  return sectionBg === "dark" || sectionBg === "red";
}

/** Body copy in content blocks — Figma simple content: #6F6F70 on light/grey, #F4F4F4 on dark. */
export function getBodyTextClass(sectionBg?: BlockSectionBg | null): string {
  if (isTintedSectionBg(sectionBg)) {
    return "text-header-top-fg";
  }
  return "text-tag-foreground";
}

/** Overrides MarkdownRenderer paragraph defaults (`text-foreground`) on tinted sections. */
export function getMarkdownTextOverrideClass(
  sectionBg?: BlockSectionBg | null
): string {
  if (isTintedSectionBg(sectionBg)) {
    return "[&_p]:!text-header-top-fg [&_li]:!text-header-top-fg [&_strong]:!text-header-top-fg";
  }
  return "[&_p]:!text-tag-foreground [&_li]:!text-tag-foreground [&_strong]:!text-tag-foreground";
}

/** Markdown body styles for block descriptions on any section background. */
export function getMarkdownProseClass(sectionBg?: BlockSectionBg | null): string {
  return cn(
    getBodyTextClass(sectionBg),
    getMarkdownTextOverrideClass(sectionBg)
  );
}

/** Scope class for BlockWrapper / full-width sections with light text on dark or red backgrounds. */
export function getTintedSectionScopeClass(
  sectionBg?: BlockSectionBg | null
): string | null {
  if (sectionBg === "dark") return "block-section-dark";
  if (sectionBg === "red") return "block-section-red";
  return null;
}

/** Section grid borders — #6F6F70 via `--border` / `border-border`. */
export function getSectionBorderClass(_sectionBg?: BlockSectionBg | null): string {
  return "border-border";
}

/**
 * Vertical frame lines (Figma main-1440): `CONTENT_FRAME` on BlockWrapper and standalone block shells.
 * `<main>` stays full-bleed; blocks use `CONTENT_FRAME` for side lines + internal `getSectionGridShellClass()` (gap-px).
 */

/** Section cell background — must match BlockWrapper surface for gap-px grids. */
export function getSectionSurfaceClass(
  sectionBg?: BlockSectionBg | null
): string {
  if (sectionBg === "dark") return "bg-secondary";
  if (sectionBg === "muted") return "bg-muted";
  if (sectionBg === "red") return "bg-primary";
  return "bg-background";
}

/**
 * Figma section grids: 1px dividers via gap + bg-border.
 * Prefer this over per-cell border-r/border-l — adjacent borders always double.
 */
export function getSectionGridShellClass(
  _sectionBg?: BlockSectionBg | null
): string {
  return "grid w-full min-w-0 gap-px overflow-hidden bg-border";
}

/** Cell surface inside getSectionGridShellClass(). */
export function getSectionGridCellClass(
  sectionBg?: BlockSectionBg | null
): string {
  return getSectionSurfaceClass(sectionBg);
}

/** Desktop: grayscale by default, color on card hover. Mobile/tablet: always color. */
export const sectionGridCardImageClass =
  "h-full w-full object-cover transition-all duration-300 desktop:grayscale desktop:group-hover:grayscale-0";

/** Placeholder cells for incomplete last row in a section grid. */
export function getSectionGridRowFillerCount(
  itemsCount: number,
  columns: number
): number {
  if (columns <= 0) return 0;
  const remainder = itemsCount % columns;
  if (remainder === 0) return 0;
  return columns - remainder;
}

export function getSectionGridDesktopColumnsClass(columns: number): string {
  if (columns === 1) return "desktop:grid-cols-1";
  if (columns === 2) return "desktop:grid-cols-2";
  if (columns === 4) return "desktop:grid-cols-4";
  return "desktop:grid-cols-3";
}

export function getSectionGridHeaderDesktopColSpanClass(
  columns: number,
  hasTagline: boolean
): string {
  if (!hasTagline) {
    if (columns === 1) return "desktop:col-span-1";
    if (columns === 2) return "desktop:col-span-2";
    if (columns === 4) return "desktop:col-span-4";
    return "desktop:col-span-3";
  }

  const span = columns - 1;
  if (span === 1) return "desktop:col-span-1";
  if (span === 3) return "desktop:col-span-3";
  return "desktop:col-span-2";
}

/**
 * Legacy flex split (1/3 + 2/3): divider only on the leading column (border-r).
 * Never add border-l on the trailing column at the same junction.
 */
export function getSectionSplitLeadingClass(
  sectionBg?: BlockSectionBg | null
): string {
  return cn("border-r", getSectionBorderClass(sectionBg));
}

/** Horizontal rules (Separator) — matches section borders. */
export function getSectionDividerBgClass(_sectionBg?: BlockSectionBg | null): string {
  return "bg-border";
}

/** Maps page_blocks.background to BlockWrapper.bgColor (undefined = default light page background). */
export function sectionBgToWrapper(
  sectionBg?: BlockSectionBg | null
): BlockWrapperBg | undefined {
  if (sectionBg == null || sectionBg === "light" || sectionBg === "default") {
    return undefined;
  }
  return sectionBg;
}

/** Standard padding for block text content (headline, description). */
export function getSectionContentPaddingClass(): string {
  return "px-4 py-10 md:px-6 md:py-10 desktop:px-10 desktop:py-20";
}

/** Mobile/tablet padding for split block headers (tagline + headline row). */
export function getSectionHeaderMobilePaddingClass(): string {
  return "px-4 py-10 md:px-6 md:pb-12";
}

/** Equal padding on all sides — image cells in section grids. */
export function getSectionImageCellPaddingClass(): string {
  return "p-4 md:p-6 desktop:p-10";
}

/**
 * Tagline in 1/3 sidebar — top-aligned (Figma Accordion).
 * Parent must use `flex items-start` so SectionTag is not vertically centered in a tall row.
 * Add getSectionHeaderMobilePaddingClass() on the same cell when tagline is visible on mobile.
 */
export function getSectionTaglineSidebarClass(options?: {
  hiddenOnMobile?: boolean;
}): string {
  return cn(
    "items-start",
    options?.hiddenOnMobile ? "hidden desktop:flex" : "flex",
    "desktop:px-10 desktop:py-0 desktop:pt-24 desktop:pb-0"
  );
}
